import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { papersService } from '../../services/papersService';

export const FilterSection = ({ title, items, selected, onChange }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [errorState, setErrorState] = useState(false);
    const [lastErrorTime, setLastErrorTime] = useState(0);
    const ERROR_RETRY_DELAY = 30000; // 30초 후에 재시도
    const observerRef = useRef();
    const containerRef = useRef();
    const isAuthorsSection = title.toLowerCase() === 'authors';

    // Debounce function
    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    };

    // 검색어 변경 시 API 호출을 실행하는 함수
    const fetchAuthorsWithSearch = useCallback(async (term, pageNum = 1) => {
        if (!isAuthorsSection) return;

        // 마지막 에러 이후 일정 시간이 지나지 않았으면 재시도하지 않음
        const now = Date.now();
        if (errorState && (now - lastErrorTime < ERROR_RETRY_DELAY)) {
            return;
        }

        try {
            setLoading(true);
            const response = await papersService.getAuthors(pageNum, 20, term);

            if (pageNum === 1) {
                setAuthors(response.authors || []);
            } else {
                setAuthors(prev => [...prev, ...(response.authors || [])]);
            }

            setHasMore((response.authors || []).length === 20);
            setErrorState(false); // 성공 시 에러 상태 초기화
        } catch (error) {
            console.error('Error fetching authors:', error);
            setAuthors([]);
            setHasMore(false);
            setErrorState(true); // 에러 상태 설정
            setLastErrorTime(Date.now()); // 에러 발생 시간 기록
        } finally {
            setLoading(false);
        }
    }, [isAuthorsSection, errorState, lastErrorTime]);

    // 검색어 변경 핸들러 with debounce
    const debouncedSearch = useCallback(
        debounce((value) => {
            setPage(1);
            fetchAuthorsWithSearch(value, 1);
        }, 300),
        [fetchAuthorsWithSearch]
    );

    // 필터링된 아이템 목록 계산 (author가 아닌 경우)
    const filteredItems = items.filter(item =>
        typeof item === 'string'
            ? item.toLowerCase().includes(searchTerm.toLowerCase())
            : item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 선택된 항목이 있는지 확인
    const hasSelectedItems = selected.length > 0;

    // Intersection Observer 설정
    useEffect(() => {
        if (!isAuthorsSection || !observerRef.current || !containerRef.current || loading) return;

        const handleObserver = (entries) => {
            const [entry] = entries;
            if (!entry.isIntersecting) return;

            // Check if we have enough content to scroll
            const containerHeight = containerRef.current.clientHeight;
            const contentHeight = containerRef.current.scrollHeight;

            // Only load more if we've filled the container and have more items
            if (contentHeight > containerHeight && hasMore && !loading && authors.length > 0) {
                setPage(prev => prev + 1);
            }
        };

        const observer = new IntersectionObserver(handleObserver, {
            root: containerRef.current,
            rootMargin: '50px',
            threshold: 0.1
        });

        observer.observe(observerRef.current);
        return () => observer.disconnect();
    }, [hasMore, loading, authors.length, isAuthorsSection]);

    // Authors 데이터 가져오기 - 페이지 변경 시에만 호출
    useEffect(() => {
        if (!isAuthorsSection) return;
        if (page === 1) return; // 페이지가 1일 때는 검색어 변경 시 호출되므로 중복 호출 방지

        fetchAuthorsWithSearch(searchTerm, page);
    }, [page, isAuthorsSection, fetchAuthorsWithSearch, searchTerm]);

    // 초기 데이터 로드
    useEffect(() => {
        if (isAuthorsSection && authors.length === 0 && !loading && searchTerm.length === 0 && !errorState) {
            console.log("fetchAuthorsWithSearch");
            fetchAuthorsWithSearch('', 1);
        }
    }, [isAuthorsSection, authors.length, loading, fetchAuthorsWithSearch, searchTerm, errorState]);

    const displayItems = isAuthorsSection ? authors : filteredItems;
    console.log("displayItems.length:" + displayItems.length);

    // 아이템이 10개 이상인지 확인
    const shouldShowSearch = (items.length >= 5) || isAuthorsSection;

    return (
        <div className="mb-6 border p-3">
            <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    {title}
                    {hasSelectedItems && (
                        <span className="ml-2 text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                            {selected.length}
                        </span>
                    )}
                </h3>
                {isCollapsed ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                )}
            </div>

            {!isCollapsed && (
                <>
                    {shouldShowSearch && (
                        <div className="my-2">
                            <input
                                type="text"
                                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                placeholder={`Search ${title.toLowerCase()}...`}
                                value={searchTerm}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchTerm(value);
                                    if (isAuthorsSection) {
                                        debouncedSearch(value);
                                    }
                                }}
                                // 알아서 닫히는 문제 수정
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    <div
                        ref={containerRef}
                        className="space-y-2 max-h-48 overflow-y-auto mt-2"
                    >
                        {displayItems.length > 0 ? (
                            displayItems.map((item, index) => (
                                <label key={index} className="flex items-center justify-between hover:bg-gray-50 px-2 py-1 rounded" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox text-primary-600 rounded"
                                            checked={selected.includes(typeof item === 'string' ? item : item.name)}
                                            onChange={(e) => {
                                                const value = typeof item === 'string' ? item : item.name;
                                                if (e.target.checked) {
                                                    onChange([...selected, value]);
                                                } else {
                                                    onChange(selected.filter(i => i !== value));
                                                }
                                                e.stopPropagation(); // 체크박스 이벤트도 전파 방지
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            onTouchEnd={(e) => e.stopPropagation()}
                                        />
                                        <span className="text-sm text-gray-700 ml-2">
                                            {typeof item === 'string' ? item : item.name}
                                        </span>
                                    </div>
                                    {typeof item !== 'string' && item.count && (
                                        <span className="text-xs text-gray-500">
                                            ({item.count})
                                        </span>
                                    )}
                                </label>
                            ))
                        ) : (
                            <div className="text-center py-2 text-sm text-gray-500">
                                {loading ? 'Loading...' : errorState ?
                                    <div>
                                        <div>Server connection error</div>
                                        <button
                                            onClick={() => {
                                                setErrorState(false);
                                                fetchAuthorsWithSearch(searchTerm, 1);
                                            }}
                                            className="mt-2 text-primary-600 underline text-xs"
                                        >
                                            Retry now
                                        </button>
                                    </div> : 'No items found'}
                            </div>
                        )}
                        {loading && (
                            <div className="text-center py-2 text-sm text-gray-500">
                                Loading...
                            </div>
                        )}
                        <div ref={observerRef} style={{ height: '1px' }} />
                    </div>
                </>
            )}
        </div>
    );
};